import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Heading, Text, Icon } from '@chakra-ui/react';
import { FiShield } from 'react-icons/fi';
import ReactPaginate from 'react-paginate';
import { AuditFilters, AuditTable, AuditDetailModal } from 'components/auditoria';
import { getAuditLogs } from 'api/administrador/auditoria';
import useAuth from 'hooks/useAuth';

export default function AuditoriaAdmin() {
  const { user } = useAuth();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    searchMode: 'responsable',
    dateRange: '30',
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    dateTo: new Date().toISOString(),
    action: '',
  });

  const pageSize = 10;

  const loadAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAuditLogs({
        page: currentPage,
        pageSize,
        filters: {
          search: filters.search,
          searchMode: filters.searchMode,
          action: filters.action,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      });

      setLogs(response.data || []);
      setTotalPages(response.meta?.pagination?.pageCount || 0);
      setTotalResults(response.meta?.pagination?.total || 0);
    } catch (error) {
      console.error('Error cargando audit logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleViewDetail = (log) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handlePageChange = (event) => {
    setCurrentPage(event.selected + 1);
  };

  // Solo accesible para usuarios con rol supervisor_admin
  if (user?.role?.type !== 'supervisor_admin') {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          No tienes permisos para acceder a esta sección.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Box bg="linear-gradient(135deg, #574feb 0%, #7c3aed 50%, #8b5cf6 100%)" p={5}>
          <Flex align="center" gap={3}>
            <Icon as={FiShield} boxSize={6} color="white" />
            <Heading size="md" color="white" textTransform="uppercase" letterSpacing="wide">
              Auditoría de Acciones
            </Heading>
          </Flex>
        </Box>

        <AuditFilters filters={filters} onFilterChange={handleFilterChange} />

        <AuditTable logs={logs} loading={loading} onViewDetail={handleViewDetail} />

        <Flex justify="space-between" align="center" p={4} borderTop="1px solid" borderColor="gray.100">
          <Text color="gray.600" fontSize="sm">
            Mostrando {(currentPage - 1) * pageSize + 1} a {Math.min(currentPage * pageSize, totalResults)} de{' '}
            {totalResults} resultados
          </Text>
          {totalPages > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              previousLabel="<"
              onPageChange={handlePageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              forcePage={currentPage - 1}
              renderOnZeroPageCount={null}
              containerClassName="auditPagination"
              pageClassName="auditPaginationItem"
              activeClassName="auditPaginationActive"
              previousClassName="auditPaginationItem"
              nextClassName="auditPaginationItem"
              disabledClassName="auditPaginationDisabled"
            />
          )}
        </Flex>
      </Box>

      <AuditDetailModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} log={selectedLog} />

      <style jsx global>{`
        .auditPagination {
          display: flex;
          list-style: none;
          gap: 4px;
          margin: 0;
          padding: 0;
        }
        .auditPaginationItem {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 36px;
          height: 36px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          color: #4a5568;
          transition: all 0.2s;
        }
        .auditPaginationItem:hover {
          background-color: #f7fafc;
          border-color: #cbd5e0;
        }
        .auditPaginationItem a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          padding: 0 12px;
        }
        .auditPaginationActive {
          background-color: #1a202c !important;
          border-color: #1a202c !important;
          color: white !important;
        }
        .auditPaginationActive a {
          color: white !important;
        }
        .auditPaginationDisabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .auditPaginationDisabled:hover {
          background-color: transparent;
        }
      `}</style>
    </Box>
  );
}
